import { type AnchorHTMLAttributes, forwardRef, type ReactNode } from "react"
import { Link as RouterLink, useInRouterContext } from "react-router"

/**
 * `next/link` shim — React Router implementation (NEX-248 Phase C).
 *
 * Imported by `@/common/navigation/Link`. Accepts the next/link prop shape the
 * app passes through, strips the Next-only props React Router has no concept of,
 * and renders an `<a>` via React Router's `<Link>`.
 *
 * Outside a Router context (isolated unit tests, or anything rendered before the
 * router mounts) it degrades to a plain `<a>` — matching next/link, which never
 * required a provider — so consumers stay renderable everywhere.
 */

type UrlObject = { pathname?: string; query?: Record<string, string | number | undefined> }

interface NextLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  href: string | UrlObject
  children?: ReactNode
  // Next-only props — accepted then ignored so callers compile unchanged.
  replace?: boolean
  scroll?: boolean
  shallow?: boolean
  passHref?: boolean
  prefetch?: boolean
  locale?: string | false
  legacyBehavior?: boolean
}

function toHref(href: string | UrlObject): string {
  if (typeof href === "string") return href
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(href.query ?? {})) {
    if (value !== undefined) search.set(key, String(value))
  }
  const qs = search.toString()
  return `${href.pathname ?? ""}${qs ? `?${qs}` : ""}`
}

const NextLink = forwardRef<HTMLAnchorElement, NextLinkProps>(function NextLink(
  { href, replace, scroll, shallow, passHref, prefetch, locale, legacyBehavior, children, ...rest },
  ref
) {
  void scroll
  void shallow
  void passHref
  void prefetch
  void locale
  void legacyBehavior
  const to = toHref(href)
  const inRouter = useInRouterContext()

  if (!inRouter) {
    return (
      <a ref={ref} href={to} {...rest}>
        {children}
      </a>
    )
  }

  return (
    <RouterLink ref={ref} to={to} replace={replace} {...rest}>
      {children}
    </RouterLink>
  )
})

export default NextLink
